
import React from 'react';
import { Card } from "@/components/ui/card";
import { TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, ComposedChart } from 'recharts';
import type { PerformanceData } from './types';
import { useIsMobile } from '@/hooks/use-mobile';

interface PerformanceTrendChartProps {
  performanceData: PerformanceData[];
}

export const PerformanceTrendChart: React.FC<PerformanceTrendChartProps> = ({ performanceData }) => {
  const { isMobile } = useIsMobile();
  
  const trendData = React.useMemo(() => 
    performanceData.map(data => ({
      date: new Date(data.created_at).toLocaleDateString(),
      accuracy: (data.correct_answers / data.total_questions) * 100,
      difficulty: data.difficulty
    })),
    [performanceData]
  );

  return (
    <Card className={`${isMobile ? 'p-3' : 'p-6'} bg-gradient-to-br from-purple-50 to-white hover:shadow-lg transition-shadow duration-300 border border-slate-100 shadow-sm`}>
      <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold mb-4 sm:mb-6 flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-500`}>
        <TrendingUp className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} text-purple-500`} />
        Performance Trend Over Time
      </h2>
      <div className={`h-[${isMobile ? '250px' : '300px'}] mt-4`}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart 
            data={trendData} 
            margin={isMobile ? 
              { top: 10, right: 5, left: -20, bottom: 20 } : 
              { top: 10, right: 10, left: 0, bottom: 20 }}
          >
            <defs>
              <linearGradient id="colorAccuracy" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
              </linearGradient>
              <filter id="glow-line" height="200%" width="200%" x="-50%" y="-50%">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
            <XAxis 
              dataKey="date" 
              stroke="#6B7280"
              tick={{ fill: '#6B7280', fontSize: isMobile ? 9 : 12 }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={{ stroke: '#E5E7EB' }}
              tickCount={isMobile ? 4 : undefined}
              angle={isMobile ? -25 : 0}
              textAnchor={isMobile ? "end" : "middle"}
              height={isMobile ? 50 : 30}
            />
            <YAxis 
              yAxisId="accuracy"
              label={isMobile ? undefined : { 
                value: 'Accuracy (%)', 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: '#6B7280', fontWeight: 500 }
              }}
              stroke="#6B7280"
              tick={{ fill: '#6B7280', fontSize: isMobile ? 10 : 12 }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={{ stroke: '#E5E7EB' }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                padding: isMobile ? '4px 8px' : '8px 12px',
                fontSize: isMobile ? '10px' : '12px'
              }}
              formatter={(value: number, name: string) => {
                if (name === 'accuracy') return [`${value.toFixed(1)}%`, 'Accuracy'];
                return [value, name];
              }}
              labelFormatter={(date) => `Date: ${date}`}
              cursor={{ stroke: '#8B5CF6', strokeWidth: 1, strokeDasharray: '5 5' }}
            />
            
            {/* Shaded area under the line */}
            <Area
              type="monotone"
              dataKey="accuracy"
              yAxisId="accuracy"
              fill="url(#colorAccuracy)"
              fillOpacity={0.6}
              stroke="none"
            />
            
            {/* Main performance line */}
            <Line 
              type="monotone" 
              dataKey="accuracy"
              yAxisId="accuracy"
              stroke="#8B5CF6" 
              strokeWidth={isMobile ? 2 : 3}
              dot={(props) => {
                const difficulty = props.payload.difficulty;
                let fill;
                switch(difficulty) {
                  case 'easy': fill = '#4ADE80'; break;
                  case 'medium': fill = '#F59E0B'; break;
                  case 'hard': fill = '#EF4444'; break;
                  default: fill = '#8B5CF6';
                }
                
                return (
                  <circle 
                    cx={props.cx} 
                    cy={props.cy} 
                    r={isMobile ? 3 : 5} 
                    fill={fill} 
                    stroke="#FFF" 
                    strokeWidth={2}
                    style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.2))' }}
                  />
                );
              }}
              activeDot={{ 
                r: isMobile ? 6 : 8, 
                fill: '#8B5CF6', 
                stroke: '#FFF',
                strokeWidth: 2
              }}
              filter={isMobile ? undefined : "url(#glow-line)"}
            />
            
            {/* Reference lines - only show on desktop */}
            {!isMobile && (
              <>
                <ReferenceLine 
                  y={80} 
                  yAxisId="accuracy"
                  stroke="#16A34A" 
                  strokeDasharray="4 4" 
                  label={{ 
                    value: 'Excellent (80%)', 
                    position: 'left', 
                    fill: '#16A34A',
                    fontSize: 12
                  }} 
                />
                <ReferenceLine 
                  y={60} 
                  yAxisId="accuracy"
                  stroke="#F59E0B" 
                  strokeDasharray="4 4" 
                  label={{ 
                    value: 'Good (60%)', 
                    position: 'left', 
                    fill: '#F59E0B',
                    fontSize: 12 
                  }} 
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {/* Legend for difficulty colors */}
      <div className={`mt-4 flex justify-center gap-${isMobile ? '4' : '6'}`}>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-600`}>Easy</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
          <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-600`}>Medium</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-600`}>Hard</span>
        </div>
      </div>
    </Card>
  );
};
